'use client'

import ShimmerLogo from '@/app/[locale]/[roomId]/components/ShimmerLogo'
import { cn } from '@/lib/utils'
import { motion } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import { useEventListener } from 'usehooks-ts'

// Generate random star positions using fixed large range like original SCSS
const generateShadows = (count: number, radius: number, blur: boolean = true) => {
  let shadow = ''
  for (let i = 0; i < count; i++) {
    const x = Math.random() * 2000
    const y = Math.random() * 2000
    if (blur) {
      const blurRadius = radius * 0.1 + Math.random() * radius
      shadow += `${x}px ${y}px ${blurRadius}px ${radius * 0.1}px #C5C5C5`
    } else {
      shadow += `${x}px ${y}px #C5C5C5`
    }
    if (i < count - 1) shadow += ', '
  }
  return shadow
}

type WorldSplashProps = {
  isVisible: boolean
  className?: string
}

const WorldSplash = ({ isVisible, className }: WorldSplashProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [shadows, setShadows] = useState({
    small: '',
    medium: '',
    large: '',
    largeClear: '',
  })
  const updateShadows = () => {
    setShadows({
      small: generateShadows(700, 1, true),
      medium: generateShadows(300, 2, true),
      large: generateShadows(20, 3, true),
      largeClear: generateShadows(10, 3, false),
    })
  }

  useEffect(() => {
    updateShadows()
  }, [])

  useEventListener('resize', updateShadows)

  return (
    <motion.div
      ref={containerRef}
      className={cn('h-full w-full overflow-hidden', className)}
      style={{ background: 'radial-gradient(ellipse at bottom, #1b2735 0%, #090a0f 100%)' }}
      initial={{ opacity: 0, display: 'block' }}
      animate={{
        opacity: isVisible ? 1 : 0,
        display: isVisible ? 'block' : 'none',
      }}
      transition={{
        duration: 0.6,
        ease: [0.4, 0, 0.2, 1],
      }}
    >
      {/* Small blur stars */}
      <div
        className="absolute h-px w-px animate-[stars-float-up_300s_linear_infinite] rounded-full bg-transparent blur-[0.5px]"
        style={{
          left: '500px',
          top: '500px',
          boxShadow: shadows.small,
        }}
      >
        <div
          className="absolute h-px w-px bg-transparent"
          style={{
            top: '2000px',
            boxShadow: shadows.small,
          }}
        />
      </div>

      {/* Medium blur stars */}
      <div
        className="absolute h-0.5 w-0.5 animate-[stars-float-up_200s_linear_infinite] rounded-full bg-transparent blur-[0.5px]"
        style={{ boxShadow: shadows.medium }}
      >
        <div
          className="absolute h-0.5 w-0.5 bg-transparent"
          style={{
            top: '2000px',
            boxShadow: shadows.medium,
          }}
        />
      </div>

      {/* Large blur stars */}
      <div
        className="relative h-[3px] w-[3px] animate-[stars-float-up_100s_linear_infinite] rounded-full bg-transparent"
        style={{ boxShadow: shadows.large }}
      >
        <div
          className="absolute h-[3px] w-[3px] bg-transparent"
          style={{
            top: '2000px',
            boxShadow: shadows.large,
          }}
        />
      </div>

      {/* Large clear stars */}
      <div
        className="relative h-[3px] w-[3px] animate-[stars-float-up_100s_linear_infinite] rounded-full bg-transparent"
        style={{ boxShadow: shadows.largeClear }}
      >
        <div
          className="absolute h-[3px] w-[3px] bg-transparent"
          style={{
            top: '2000px',
            boxShadow: shadows.largeClear,
          }}
        />
      </div>

      {/* Centered Logo with Shimmer Effect */}
      <div className="absolute inset-0 z-10 flex items-center justify-center">
        <div className="relative">
          <ShimmerLogo />
        </div>
      </div>

      <style jsx scoped>{`
        @keyframes stars-float-up {
          from {
            transform: translateY(0px);
          }
          to {
            transform: translateY(-6000px);
          }
        }
      `}</style>
    </motion.div>
  )
}

export default WorldSplash
